// Mock data fixtures from the prototype, retained during Phase A wire-up.
// Replaced by useDashboardData() against Convex queries during A.2.
//
// Also exports buildMockDashboardData() — produces the same shape that
// useDashboardData() returns, sourced from the MOCK_* fixtures below.
// PlayerDashboard uses it as its fallback when no live data is supplied.

export const MOCK_USER = { name: 'Wren', handle: '@wren', initial: 'W' };

export const MOCK_ACTIVE_CHARACTER = {
  id: 'shenyi',
  charId: 'shenyi',
  name: 'Shenyi',
  race: 'Half-Elf',
  charClass: 'Cleric',
  level: 5,
  campaignId: 'thunder-king',
  avatar: '🧙‍♀️',
  hp: 38, maxHp: 42,
  mp: 8, maxMp: 12,
};

export const MOCK_CAMPAIGNS = [
  {
    id: 'lockhood', name: 'Lockhood', dm: 'Wren', role: 'owner',
    players: 3, schedule: 'Wednesdays · 8pm',
    blurb: 'Your own table — a frontier village under siege by sleepless things.',
    party: ['Viru', 'Aribella', 'Halrek'], yourChar: null, icon: '✦', rune: 'astral',
    paletteIndex: 0, // accent (on home view)
    hueShift: 0,     // base theme accent — Lockhood sits at the theme's own hue
  },
  {
    id: 'thunder-king', name: 'Call of the Thunder King', dm: 'Thomas Renwick', role: 'player',
    players: 4, schedule: 'Mondays · 7pm',
    blurb: 'A nine-towered fortress hums above the storm — someone has been counting the lightning.',
    party: ['Shenyi', 'Dou Lan', 'The Ball', 'Vesh'], yourChar: 'Shenyi', myCharacterId: 'shenyi', icon: '⚡', rune: 'tech',
    paletteIndex: 1, // danger (on home view)
    hueShift: 25,    // a warm spinoff of the theme accent
  },
  {
    id: 'abyss', name: 'Escape the Abyss', dm: 'Selene Vale', role: 'player',
    players: 4, schedule: 'Saturdays · 6pm',
    blurb: 'You woke without names on a ship that does not surface. Something else is captain.',
    party: ['Agni', 'Darrick', 'Mim'], yourChar: 'Kali', myCharacterId: 'kali-c', icon: '🌊', rune: 'graph',
    paletteIndex: 2, // info (on home view)
    hueShift: -25,   // a cool spinoff of the theme accent
  },
];

export const MOCK_TABLE_NOTES = [
  {
    id: 1, pinned: true,
    title: "Tonight's session moved to 8:30pm",
    body: 'Running 30 min late — grab snacks, we open in the tower bell-room.',
    author: 'Thomas Renwick', campaignId: 'thunder-king', timeAgo: '2h ago',
  },
  {
    id: 2, pinned: false,
    title: 'Charter docs are in the journal',
    body: 'Read before Saturday. The Thrice-Sailed will not wait for slow scholars.',
    author: 'Selene Vale', campaignId: 'abyss', timeAgo: 'yesterday',
  },
];

export const MOCK_SOLO_CHARS = [
  { id: 'andruil', name: 'Andruil', cls: 'L1 Paladin', hp: 49, maxHp: 49, lastPlayed: '', avatar: '⚔️', paletteIndex: 4 },
  { id: 'kali', name: 'Kali', cls: 'L2 Wizard', hp: 14, maxHp: 18, lastPlayed: '', avatar: '📖', paletteIndex: 2 },
];

// ============================================================================
// CAMPAIGN-VIEW SEED DATA
// ============================================================================

// Characters who are in a campaign (i.e. show up on the campaign's
// "My Characters" panel). Distinct from solo chars above which
// are explicitly NOT in a campaign.
export const MOCK_CAMPAIGN_CHARS = {
  'thunder-king': [
    {
      id: 'shenyi', name: 'Shenyi', race: 'Half-Elf', cls: 'Cleric',
      level: 5, hp: 38, maxHp: 42, mp: 8, maxMp: 12,
      avatar: '🧙‍♀️', note: '',
    },
  ],
  'abyss': [
    {
      id: 'kali-c', name: 'Kali', race: 'Gnome', cls: 'Wizard',
      level: 4, hp: 24, maxHp: 30, mp: 14, maxMp: 18,
      avatar: '✨', note: '',
    },
  ],
  'lockhood': [], // Wren is the DM here, no PC
};

export const MOCK_CAMPAIGN_FRIENDS = {
  'lockhood': [
    { id: 'p-anders', name: 'Anders', role: 'Player', status: 'online' },
    { id: 'p-cass',   name: 'Cass',   role: 'Player', status: 'online' },
    { id: 'p-jules',  name: 'Jules',  role: 'Player', status: 'offline' },
  ],
  'thunder-king': [
    { id: 'p-thomas', name: 'Thomas Renwick', role: 'DM',     status: 'online' },
    { id: 'p-mira',   name: 'Mira',           role: 'Player', status: 'online' },
    { id: 'p-tarn',   name: 'Tarn',           role: 'Player', status: 'online' },
    { id: 'p-veld',   name: 'Veld',           role: 'Player', status: 'offline' },
  ],
  'abyss': [
    { id: 'p-selene', name: 'Selene Vale',    role: 'DM',     status: 'online' },
    { id: 'p-noor',   name: 'Noor',           role: 'Player', status: 'online' },
    { id: 'p-rell',   name: 'Rell',           role: 'Player', status: 'offline' },
    { id: 'p-yan',    name: 'Yan',            role: 'Player', status: 'offline' },
  ],
};

// Extra campaign metadata used inside the campaign view.
export const MOCK_CAMPAIGN_EXTRA = {
  'lockhood':     { day: 23, sessionCount: 23, invite: 'HOLL-CRWN-77AT', quest: null },
  'thunder-king': { day: 18, sessionCount: 18, invite: 'CRMS-PACT-91XK', quest: 'Eye of Askesh' },
  'abyss':        { day:  4, sessionCount:  4, invite: 'ABYS-SAIL-04QQ', quest: 'Charter passage on the Thrice-Sailed' },
};

// ============================================================================
// DATE / SCHEDULE HELPERS
// ============================================================================

// Mock "today" for the countdown helper. Keeping a fixed pretend day means
// "Tonight" reads consistently on the Lockhood card during demos.

// Synthesize the dashboard data shape from the MOCK_* fixtures. Mirrors
// the contract that useDashboardData() returns at runtime so the views
// can be exercised against either source interchangeably.
export const buildMockDashboardData = () => {
  // Per-campaign announcement map mirrors the live hook's shape:
  // group MOCK_TABLE_NOTES by campaignId so the campaign detail view can
  // read announcementsForCampaign[id] in mock mode the same way it does
  // against Convex.
  const announcementsForCampaign = {};
  for (const note of MOCK_TABLE_NOTES) {
    if (!announcementsForCampaign[note.campaignId]) {
      announcementsForCampaign[note.campaignId] = [];
    }
    announcementsForCampaign[note.campaignId].push(note);
  }
  return {
    loading: false,
    user: MOCK_USER,
    activeCharacter: MOCK_ACTIVE_CHARACTER,
    campaigns: MOCK_CAMPAIGNS,
    soloCharacters: MOCK_SOLO_CHARS,
    tableNotes: MOCK_TABLE_NOTES,
    announcementsForCampaign,
    campaignCharacters: MOCK_CAMPAIGN_CHARS,
    campaignFriends: MOCK_CAMPAIGN_FRIENDS,
    campaignExtra: MOCK_CAMPAIGN_EXTRA,
    activeCampaignId: MOCK_ACTIVE_CHARACTER?.campaignId || null,
  };
};
